import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {USER_DATA} from 'user/user-prop-types';
import {connect} from 'react-redux';
import {v4 as uuidV4} from 'uuid';
import FormGroup from '@spothero/ui/form-group';
import TextButton from '@spothero/ui/text-button';
import {push} from 'router/router-actions';
import {changeMultiRate} from 'spot/spot-actions';
import MultiRateChooser from 'spot-details/reservation-details/MultiRateChooser';
import PhoneNumber from 'user/phone/PhoneNumber';
import VehicleList from 'user/vehicle/VehicleList';
import FormatUtils from 'utils/format';
import {showChangeDateTime} from '../checkout-actions';
import CheckoutUtils from '../checkout-utils';

class TransientReservation extends Component {
    static propTypes = {
        isOpen: PropTypes.bool.isRequired,
        rid: PropTypes.number,
        user: PropTypes.shape(USER_DATA).isRequired,
        starts: PropTypes.string.isRequired,
        ends: PropTypes.string.isRequired,
        spotId: PropTypes.number,
        selectedRate: PropTypes.object.isRequired,
        hourlyRates: PropTypes.arrayOf(PropTypes.object).isRequired,
        monthlyAvailable: PropTypes.bool,
        facilityUrl: PropTypes.string.isRequired,
        licensePlateRequired: PropTypes.bool,
        phoneNumberRequired: PropTypes.bool,
        vehicleSelectPresent: PropTypes.bool,
        vehicleSelectRequired: PropTypes.bool,
        vehicleProfileId: PropTypes.number,
        isVehicleUnknown: PropTypes.bool,
        oversizedFeeMessage: PropTypes.string,
        smsParkingPass: PropTypes.bool,
        onIDontKnowChanged: PropTypes.func.isRequired,
        onMultiRateChange: PropTypes.func.isRequired,
        onChangeDateTimeClick: PropTypes.func.isRequired,
        pushTo: PropTypes.func.isRequired,
        currencyType: PropTypes.string,
        supportsOversizeFeeType: PropTypes.bool,
        onVehicleChange: PropTypes.func,
        isLoadingRates: PropTypes.bool.isRequired,
    };
    state = {
        prevUser: this.props.user,
        vehicleListKey: uuidV4(),
    };

    constructor(props) {
        super(props);

        const {hourlyRates, rid, onMultiRateChange} = props;

        if (rid) {
            onMultiRateChange({
                newRateRule: rid,
                rates: hourlyRates,
            });
        }
    }

    static getDerivedStateFromProps(nextProps, prevState) {
        const {user} = nextProps;
        const {prevUser} = prevState;

        if (prevUser.status !== user.status || prevUser.email !== user.email) {
            return {
                prevUser: user,
                vehicleListKey: uuidV4(),
            };
        }

        return null;
    }

    _onMultiRateChange = rid => {
        const {hourlyRates, onMultiRateChange} = this.props;

        onMultiRateChange({
            newRateRule: rid,
            rates: hourlyRates,
        });
    };

    _onReservationTermClick = () => {
        const {starts, facilityUrl, pushTo} = this.props;

        pushTo(
            CheckoutUtils.buildURLFromParams(facilityUrl, {
                monthly: true,
                starts,
            })
        );
    };

    vehicleSelectForm() {
        // note: this returns a promise
        return this._vehicleSelectForm
            ? this._vehicleSelectForm.submit()
            : null;
    }

    get phoneForm() {
        return this._phoneForm;
    }

    render() {
        const {
            user,
            isOpen,
            starts,
            ends,
            spotId,
            selectedRate,
            hourlyRates,
            monthlyAvailable,
            oversizedFeeMessage,
            phoneNumberRequired,
            smsParkingPass,
            vehicleSelectPresent,
            vehicleProfileId,
            isVehicleUnknown,
            vehicleSelectRequired,
            licensePlateRequired,
            onIDontKnowChanged,
            onChangeDateTimeClick,
            currencyType,
            supportsOversizeFeeType,
            onVehicleChange,
            isLoadingRates,
        } = this.props;
        const {vehicleListKey} = this.state;
        const userHasVehicles = Boolean(user.vehicles && user.vehicles.length);
        const {
            starts: rateStarts,
            ends: rateEnds,
            price_breakdown: priceBreakdown,
        } = selectedRate;
        const oversizeFee = CheckoutUtils.getOversizeFeeFromPriceBreakdown(
            priceBreakdown
        );
        const {
            startDateTimeFormatted,
            endDateTimeFormatted,
        } = CheckoutUtils.getTransientReservationInfo({
            reservationStarts: starts,
            reservationEnds: ends,
            rateStarts,
            rateEnds,
        });

        return (
            <div className="TransientReservation">
                <div className="ReservationTransient">
                    {hourlyRates.length > 1 && (
                        <span>
                            <h3>Rate Details</h3>
                            <MultiRateChooser
                                selectedRate={selectedRate}
                                rates={hourlyRates}
                                onMultiRateChange={this._onMultiRateChange}
                            />
                        </span>
                    )}
                    <h3>Requested Reservation</h3>
                    <p className="Reservation-Details-option">
                        {startDateTimeFormatted} - {endDateTimeFormatted}
                        <TextButton
                            className="change-datetime TextButton-pipe-before"
                            onClick={onChangeDateTimeClick}
                            disabled={isLoadingRates}
                        >
                            Edit
                        </TextButton>
                        {monthlyAvailable && (
                            <TextButton
                                className="toggle-monthly-parking TextButton-pipe-before"
                                onClick={this._onReservationTermClick}
                                disabled={isLoadingRates}
                            >
                                Monthly Parking Options
                            </TextButton>
                        )}
                    </p>
                </div>
                <div className="ReservationDetails-additional">
                    <div className="AccountDetails-form">
                        <h3>Additional Details</h3>
                        <FormGroup>
                            <PhoneNumber
                                ref={node => {
                                    this._phoneForm = node;
                                }}
                                phoneNumberRequired={phoneNumberRequired}
                                phone={
                                    user.phoneNumber
                                        ? FormatUtils.phone(user.phoneNumber)
                                        : null
                                }
                                smsParkingPass={smsParkingPass}
                            />
                        </FormGroup>
                        {(vehicleSelectPresent ||
                            supportsOversizeFeeType ||
                            licensePlateRequired) && (
                            <VehicleList
                                key={vehicleListKey}
                                ref={node => {
                                    this._vehicleSelectForm = node;
                                }}
                                user={user}
                                edit={
                                    isOpen &&
                                    (vehicleSelectRequired ||
                                        licensePlateRequired)
                                }
                                required={
                                    vehicleSelectRequired ||
                                    licensePlateRequired
                                }
                                facilityId={spotId}
                                iDontKnowChecked={isVehicleUnknown}
                                licensePlateRequired={licensePlateRequired}
                                vehicleProfileId={vehicleProfileId}
                                defaultFeeMessage={oversizedFeeMessage}
                                isParentVisible={isOpen}
                                loadVehicleDataOnMount={userHasVehicles}
                                onIDontKnowChanged={onIDontKnowChanged}
                                currencyType={currencyType}
                                supportsOversizeFeeType={
                                    supportsOversizeFeeType
                                }
                                oversizeFee={oversizeFee}
                                checkOversized={
                                    !supportsOversizeFeeType &&
                                    vehicleSelectRequired
                                }
                                onVehicleChange={onVehicleChange}
                                isLoadingRates={isLoadingRates}
                            />
                        )}
                    </div>
                    {(licensePlateRequired ||
                        phoneNumberRequired ||
                        vehicleSelectPresent ||
                        supportsOversizeFeeType) && (
                        <div className="AccountDetails-help-text">
                            <strong>
                                Why do I need to enter my
                                {licensePlateRequired ? ' license plate' : ''}
                                {licensePlateRequired &&
                                phoneNumberRequired &&
                                !vehicleSelectPresent &&
                                !supportsOversizeFeeType
                                    ? ' and'
                                    : licensePlateRequired &&
                                      phoneNumberRequired &&
                                      (vehicleSelectPresent ||
                                          supportsOversizeFeeType)
                                    ? ', '
                                    : ''}
                                {phoneNumberRequired ? ' phone number' : ''}
                                {(licensePlateRequired ||
                                    phoneNumberRequired) &&
                                (vehicleSelectPresent ||
                                    supportsOversizeFeeType)
                                    ? ' and'
                                    : licensePlateRequired &&
                                      phoneNumberRequired &&
                                      (vehicleSelectPresent ||
                                          supportsOversizeFeeType)
                                    ? ', '
                                    : ''}
                                {vehicleSelectPresent || supportsOversizeFeeType
                                    ? ' vehicle information'
                                    : ''}
                                ?
                            </strong>
                            {licensePlateRequired && (
                                <p>
                                    This facility is monitored using your
                                    vehicle&apos;s license plate. Make sure
                                    it&apos;s correct in order to avoid being
                                    ticketed or towed.
                                </p>
                            )}
                            {phoneNumberRequired && (
                                <p>
                                    We&apos;ll need a phone number in the case
                                    the facility&apos;s staff needs to reach
                                    you.
                                </p>
                            )}
                            {vehicleSelectPresent &&
                                !supportsOversizeFeeType && (
                                    <p>
                                        This facility charges an additional fee
                                        on-site for oversized vehicles.
                                        Providing your vehicle information will
                                        allow us to assess if this applies to
                                        you.
                                    </p>
                                )}
                            {vehicleSelectPresent &&
                                supportsOversizeFeeType && (
                                    <p>
                                        This facility charges an additional fee
                                        to accommodate larger vehicles.
                                    </p>
                                )}
                        </div>
                    )}
                </div>
            </div>
        );
    }
}

const mapStateToProps = state => {
    const {
        checkout: {
            phoneNumberRequired,
            smsParkingPass,
            licensePlateRequired,
            vehicleSelectPresent,
            vehicleSelectRequired,
            vehicleProfileId,
            isVehicleUnknown,
            rid,
            currencyType,
            supportsOversizeFeeType,
        },
        searchRequest: {starts, ends},
        spot: {
            selected: {
                spotId,
                selectedRate,
                hourly_rates: hourlyRates,
                facility: {
                    monthly_available: monthlyAvailable,
                    facility_url: facilityUrl,
                },
                oversizedFeeMessage,
            },
        },
        user: {data: userData},
    } = state;

    return {
        user: userData,
        rid,
        starts,
        ends,
        spotId,
        selectedRate,
        hourlyRates,
        monthlyAvailable,
        facilityUrl,
        oversizedFeeMessage,
        phoneNumberRequired,
        smsParkingPass,
        licensePlateRequired,
        vehicleSelectPresent,
        vehicleSelectRequired,
        vehicleProfileId,
        isVehicleUnknown,
        currencyType,
        supportsOversizeFeeType,
    };
};

const mapDispatchToProps = {
    onMultiRateChange: changeMultiRate,
    onChangeDateTimeClick: showChangeDateTime,
    pushTo: push,
};

export default connect(mapStateToProps, mapDispatchToProps, null, {
    forwardRef: true,
})(TransientReservation);
